import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Brain,
  Database,
  Download,
  Eraser,
  FileDown,
  History,
  Loader2,
  Mail,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { memoryApi } from "@/lib/memory";
import { historyApi } from "@/lib/historyApi";
import { privacyApi } from "@/lib/privacyApi";
import { toast } from "sonner";

const TYPE_LABEL = {
  profile: "Profile",
  project: "Project",
  decision: "Decision",
  behavior: "Behavior",
  conversation: "Conversation",
};

function downloadJson(filename, payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1200);
}

export default function MemorySettings() {
  const { user, setMemoryEnabled } = useAuth();
  const [memories, setMemories] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [consent, setConsent] = useState(null);
  const [consentBusy, setConsentBusy] = useState(false);

  const loadMemories = () =>
    memoryApi
      .list()
      .then(setMemories)
      .catch(() => setMemories([]));

  const loadConsent = () =>
    privacyApi
      .getConsent()
      .then(setConsent)
      .catch(() => setConsent({ consent: false, basis: "no_consent" }));

  useEffect(() => {
    if (user && typeof user === "object") {
      loadMemories();
      loadConsent();
    }
  }, [user]);

  const memoryEnabled = !!user?.memory_enabled;
  const trainingOn = !!consent?.consent;
  const memoryCount = memories?.length ?? 0;

  const statusCards = useMemo(() => ([
    {
      testid: "data-status-memory",
      label: "Memory",
      value: memoryEnabled ? "On" : "Off",
      sub: memoryEnabled ? `${memoryCount} stored item${memoryCount === 1 ? "" : "s"}` : "No personalization writes",
      tone: memoryEnabled ? "accent" : "muted",
    },
    {
      testid: "data-status-training-consent",
      label: "Training consent",
      value: trainingOn ? "On" : "Off",
      sub: consent?.basis === "admin_role" ? "Admin role" : trainingOn ? "Explicit opt-in" : "Default private",
      tone: trainingOn ? "accent" : "muted",
    },
    {
      testid: "data-status-control",
      label: "User control",
      value: "Separate",
      sub: "Memory does not imply training",
      tone: "neutral",
    },
  ]), [consent?.basis, memoryCount, memoryEnabled, trainingOn]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/settings/memory" }} replace />;

  const handleToggle = async () => {
    try {
      await setMemoryEnabled(!memoryEnabled);
      toast.success(!memoryEnabled ? "Memory on" : "Memory off");
    } catch (err) {
      toast.error(err.message || "Couldn't update memory.");
    }
  };

  const handleConsentToggle = async () => {
    if (consentBusy || !consent) return;
    if (consent.basis === "admin_role") {
      toast("Admin accounts are internal training accounts. Demote the account to opt out.");
      return;
    }
    setConsentBusy(true);
    try {
      const updated = await privacyApi.setConsent(!consent.consent);
      setConsent(updated);
      toast.success(updated.consent ? "Training-data consent on." : "Training-data consent off.");
    } catch (err) {
      toast.error(err.message || "Couldn't update consent.");
    } finally {
      setConsentBusy(false);
    }
  };

  const handleDeleteMemory = async (id) => {
    if (busyId) return;
    if (!confirm("Delete this memory? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await memoryApi.remove(id);
      setMemories((prev) => (prev || []).filter((m) => m.id !== id));
      toast.success("Memory deleted.");
    } catch (err) {
      toast.error(err.message || "Couldn't delete.");
    } finally {
      setBusyId(null);
    }
  };

  const handleClearMemories = async () => {
    if (clearing) return;
    if (!confirm("Delete all stored memories? This removes personalization facts, not your chat history.")) return;
    setClearing(true);
    try {
      await memoryApi.clearAll();
      setMemories([]);
      toast.success("All memories cleared.");
    } catch (err) {
      toast.error(err.message || "Couldn't clear memories.");
    } finally {
      setClearing(false);
    }
  };

  const handleClearHistory = async () => {
    if (clearingHistory) return;
    if (!confirm("Delete all saved chat history from your account? Stored memories are separate and will remain unless you clear them too.")) return;
    setClearingHistory(true);
    try {
      const res = await historyApi.clear();
      toast.success(`Deleted ${res.deleted || 0} saved conversation${res.deleted === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err.message || "Couldn't clear chat history.");
    } finally {
      setClearingHistory(false);
    }
  };

  const handleExportData = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const [conversationHistory, latestMemories, latestConsent] = await Promise.all([
        historyApi.list().catch(() => []),
        memoryApi.list().catch(() => memories || []),
        privacyApi.getConsent().catch(() => consent),
      ]);
      const payload = {
        exported_at: new Date().toISOString(),
        account: {
          email: user.email,
          id: user.id,
          role: user.role,
        },
        data_controls: {
          memory_enabled: !!user.memory_enabled,
          training_consent: latestConsent,
          note: "Memory personalizes your replies. Training consent is a separate opt-in for future model improvement.",
        },
        memories: latestMemories || [],
        conversations: conversationHistory || [],
      };
      downloadJson(`hbm-account-data-${new Date().toISOString().slice(0, 10)}.json`, payload);
      toast.success("Account data export started.");
    } catch (err) {
      toast.error(err.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="memory-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back to chat
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase" data-testid="data-controls-header">
          Data controls
        </h1>
        <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)] truncate max-w-[40%]" data-testid="data-controls-email">
          {user.email}
        </span>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 pb-20">
        <section className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-end mb-8" data-testid="data-controls-hero">
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.34em] uppercase text-[var(--accent)] mb-3">
              Privacy · Memory · Training
            </div>
            <h2 className="font-prose text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[var(--text)]">
              Decide what HBM remembers. Decide what HBM learns from.
            </h2>
          </div>
          <p className="font-ui text-base text-[var(--text-muted)] leading-relaxed max-w-xl" data-testid="data-controls-hero-copy">
            Memory helps the assistant personalize your future replies. Training consent is separate: it controls whether saved conversations may enter the training corpus. Turning memory on does not opt you into training.
          </p>
        </section>

        <section className="grid md:grid-cols-3 gap-3 mb-8" data-testid="data-controls-status-grid">
          {statusCards.map((card) => (
            <div key={card.testid} data-testid={card.testid} className={`border px-4 py-4 ${card.tone === "accent" ? "border-[var(--accent)]" : "border-[var(--border)]"}`}>
              <div className="font-mono-ui text-[9px] tracking-[0.26em] uppercase text-[var(--text-dim)]">{card.label}</div>
              <div className={`font-mono-ui text-xl mt-2 ${card.tone === "accent" ? "text-[var(--accent)]" : "text-[var(--text)]"}`}>{card.value}</div>
              <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1">{card.sub}</div>
            </div>
          ))}
        </section>

        <section className="border border-[var(--accent)] bg-[var(--bg-glow)] p-5 mb-8" data-testid="memory-not-training-callout">
          <div className="flex items-start gap-3">
            <ShieldCheck size={16} className="text-[var(--accent)] mt-1 shrink-0" />
            <p className="font-ui text-sm text-[var(--text)] leading-relaxed">
              <span className="font-semibold">Memory ON is not training consent.</span> Memory is a private personalization layer for your own account. Training consent is a separate opt-in that allows conversations to be copied into the admin training corpus. If training consent is off, regular-user conversations stay out of that corpus.
            </p>
          </div>
        </section>

        <div className="grid lg:grid-cols-2 gap-5 mb-10">
          <ControlCard
            testid="memory-control-card"
            icon={<Brain size={17} />}
            eyebrow="Conversation memory"
            title={memoryEnabled ? "On — replies can use saved context" : "Off — replies do not use saved personal context"}
            body="When enabled, HBM can store stable non-sensitive facts such as preferences, projects, and durable context. Corrections can change what is remembered; sensitive topics are excluded by the memory pipeline."
            action={
              <SwitchButton
                testid="memory-settings-toggle"
                checked={memoryEnabled}
                onClick={handleToggle}
                label={memoryEnabled ? "Turn memory off" : "Turn memory on"}
              />
            }
          />

          <ControlCard
            testid="training-consent-section"
            icon={<Database size={17} />}
            eyebrow="Training data"
            title={consent?.basis === "admin_role" ? "On — admin training account" : trainingOn ? "On — conversations may improve HBM" : "Off — conversations are not used for training"}
            body="This controls long-term model improvement, not the current chat. HBM may still use the current conversation to answer you in context, but it will not copy regular-user chats into the training corpus unless this switch is on."
            footer={consent ? `Basis: ${consent.basis.replace(/_/g, " ")}${consent.updated_at ? ` · updated ${new Date(consent.updated_at).toLocaleDateString()}` : ""}` : "Loading consent status"}
            action={
              <SwitchButton
                testid="training-consent-toggle"
                checked={trainingOn}
                disabled={consentBusy || consent?.basis === "admin_role"}
                onClick={handleConsentToggle}
                label={trainingOn ? "Turn training off" : "Turn training on"}
              />
            }
          />
        </div>

        <section className="border border-[var(--border)] p-5 mb-10" data-testid="export-delete-section">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--accent)] inline-flex items-center gap-2 mb-2">
                <FileDown size={13} /> Export and delete
              </div>
              <h2 className="font-prose text-3xl leading-tight text-[var(--text)]">Your data leaves when you tell it to.</h2>
              <p className="font-ui text-sm text-[var(--text-muted)] leading-relaxed mt-2 max-w-3xl">
                Export includes saved conversations, memory items, and current consent settings. Deletion controls are intentionally split: chat history, stored memories, and account deletion are different actions.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionButton
              testid="data-export-button"
              icon={exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              label={exporting ? "Exporting…" : "Export data"}
              onClick={handleExportData}
              disabled={exporting}
            />
            <ActionButton
              testid="history-clear-all"
              icon={clearingHistory ? <Loader2 size={14} className="animate-spin" /> : <History size={14} />}
              label={clearingHistory ? "Deleting…" : "Delete chat history"}
              onClick={handleClearHistory}
              disabled={clearingHistory}
              danger
            />
            <ActionButton
              testid="memory-clear-all"
              icon={clearing ? <Loader2 size={14} className="animate-spin" /> : <Eraser size={14} />}
              label={clearing ? "Clearing…" : "Clear memories"}
              onClick={handleClearMemories}
              disabled={clearing || memoryCount === 0}
              danger
            />
            <a
              href={`mailto:support@humanebehavior.com?subject=${encodeURIComponent("Account deletion request")}`}
              data-testid="account-deletion-request-link"
              className="px-4 py-3 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2 text-center transition-colors"
            >
              <Mail size={14} /> Request account deletion
            </a>
          </div>
        </section>

        <section data-testid="memory-list-section">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h2 className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)]">
                What HBM remembers about you
              </h2>
              <p className="font-ui text-sm text-[var(--text-muted)] mt-2">
                These are personalization items, not training-corpus rows.
              </p>
            </div>
          </div>

          {memories === null ? (
            <p className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[var(--text-dim)]" data-testid="memory-loading">
              <Loader2 size={12} className="inline animate-spin mr-2" />
              Loading…
            </p>
          ) : memories.length === 0 ? (
            <div className="border border-dashed border-[var(--border)] p-8 text-center" data-testid="memory-empty">
              <p className="font-ui text-sm text-[var(--text-muted)] leading-relaxed">
                Nothing stored yet. HBM writes memories only when a durable, non-sensitive fact is worth carrying into future replies.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {memories.map((m) => (
                <li
                  key={m.id}
                  data-testid={`memory-item-${m.id}`}
                  className="border border-[var(--border)] px-4 py-3 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] mb-1.5">
                      {TYPE_LABEL[m.memory_type] || m.memory_type}
                      {m.tags && m.tags.length > 0 && (
                        <span className="ml-3 text-[var(--text-muted)]">
                          {m.tags.join(" · ")}
                        </span>
                      )}
                    </div>
                    <p className="font-ui text-sm text-[var(--text)] leading-relaxed">
                      {m.content}
                    </p>
                    <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString(undefined, {
                        month: "short", day: "numeric", year: "numeric",
                      }) : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteMemory(m.id)}
                    disabled={busyId === m.id}
                    data-testid={`memory-delete-${m.id}`}
                    aria-label="Delete memory"
                    className="text-[var(--text-dim)] hover:text-[var(--danger)] transition-colors disabled:opacity-40"
                  >
                    {busyId === m.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mt-10" data-testid="data-controls-support-copy">
          Questions? <a href="mailto:support@humanebehavior.com" data-testid="data-controls-support-link" className="hover:text-[var(--accent)]">support@humanebehavior.com</a>
        </p>
      </main>
    </div>
  );
}

function ControlCard({ icon, eyebrow, title, body, footer, action, testid }) {
  return (
    <section className="border border-[var(--border)] p-5 min-h-[260px] flex flex-col" data-testid={testid}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--accent)] mb-3 inline-flex items-center gap-2">
            {icon}
            {eyebrow}
          </div>
          <h2 className="font-prose text-3xl text-[var(--text)] leading-tight mb-3">
            {title}
          </h2>
        </div>
        {action}
      </div>
      <p className="font-ui text-sm text-[var(--text-muted)] leading-relaxed">
        {body}
      </p>
      {footer && (
        <p className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-auto pt-5" data-testid={`${testid}-footer`}>
          {footer}
        </p>
      )}
    </section>
  );
}

function SwitchButton({ checked, disabled, onClick, label, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-60 ${
        checked ? "bg-[var(--accent)]" : "bg-[var(--border)]"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-[var(--bg)] transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function ActionButton({ icon, label, onClick, disabled, testid, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`px-4 py-3 border font-mono-ui text-[10px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2 text-center transition-colors disabled:opacity-40 ${
        danger
          ? "border-[var(--border)] hover:border-[var(--danger)] hover:text-[var(--danger)]"
          : "border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}